import type {
  DeliverableDto,
  DeliverablesResponse,
  HomeResponse,
  MilestoneDto,
  OrderLinksDto,
  ResourceDto,
  SalesStepDto,
  SectionKey,
  SectionState
} from "../shared/dto";
import {
  RAIL_STEP_KEYS,
  displayStatusForStep,
  journeyAxesOf,
  railBackingSection,
  railJourneyKeysFor,
  type ClientSimulationSnapshot,
  type JourneyAxes,
  type RailStep,
  type RailStepKey
} from "./rail-model";

// The ?test=1 walkthrough: a simulated journey around one focused step. Split
// out of `rail-model.ts` (F171 growth tripwire) and re-exported from there, so
// every import site is unchanged. Nothing here runs at module load, which is
// what keeps the import cycle with `rail-model.ts` harmless.

export function simulateRailSteps(
  steps: RailStep[],
  focus: RailStepKey | null,
  // 🔴 F152-W2: required, for the F143 reason - the walkthrough now has a step
  // whose simulated surroundings differ by journey (`profile` sits before the
  // Buddy questionnaire in one and behind the marketing video in the other), so
  // a defaulted axis here would be a neutral value the type system cannot tell
  // from a wrong one.
  axes: JourneyAxes
): RailStep[] {
  if (!focus) return steps;
  const preserveFocusedQuestionnaireDone = (focus === "q1" || focus === "q2")
    && steps.find((step) => step.key === focus)?.state === "done";
  const states = simulationStates(focus, preserveFocusedQuestionnaireDone, axes);
  const primaryKey = simulationPrimaryKey(states, focus);
  return steps.map((step) => {
    const primaryNext = step.key === primaryKey;
    return {
      ...step,
      state: states[step.key],
      displayStatus: displayStatusForStep(step.key, states[step.key], primaryNext, states),
      primaryNext,
      trainingPhotoPulse: step.key === "training" && states.training === "working",
      // The walkthrough moves the STATE, so the arc has to follow it: a
      // simulated locked or done step must not keep the live client's ring.
      progress: states[step.key] === "locked" || states[step.key] === "done" ? null : step.progress
    };
  });
}

export function simulateClientSnapshot(
  home: HomeResponse,
  delivery: DeliverablesResponse,
  focus: RailStepKey | null
): ClientSimulationSnapshot {
  if (!focus) return { home, delivery, resources: [], milestones: [] };
  const preserveFocusedQuestionnaireDone = (focus === "q1" || focus === "q2")
    && home.sections.find((section) => section.key === focus)?.state === "done";
  const states = simulationStates(focus, preserveFocusedQuestionnaireDone, journeyAxesOf(home.client));
  const primaryKey = simulationPrimaryKey(states, focus);
  const sectionStates: Partial<Record<SectionKey, SectionState>> = {
    onboarding_call: states.onboarding_call,
    q1: states.q1,
    knowhow: states.knowhow,
    training: states.training,
    beta: sharedState(states.beta, states.feedback),
    marketing_call: states.marketing_call,
    q2: states.q2,
    sales: chapterState([
      states.strategy,
      states.salespage,
      states.emails,
      states.webinar,
      states.salespage_publish
    ]),
    stripe: states.stripe,
    handover: states.handover,
    app: appSectionState(states)
  };
  const backingSection = railBackingSection(primaryKey, journeyAxesOf(home.client));
  const sections = home.sections.map((item) => ({
    ...item,
    state: sectionStates[item.key] ?? item.state,
    primaryNext: item.key === backingSection
  }));
  const salesSteps = salesStepsFromStates(states, railJourneyKeysFor(journeyAxesOf(home.client), home.salesSteps));
  const simulatedHome: HomeResponse = {
    ...home,
    sections,
    primaryNext: backingSection,
    feedbackUnlocked: states.marketing_call === "done",
    profileConfirmed: states.profile === "done",
    appleAccountConfirmed: states.apple === "done",
    profile: states.profile === "done"
      ? home.profile.status === "ready"
        ? home.profile
        : { status: "ready", facts: [], lastUpdatedAt: simulationNow() }
      : { status: "building", facts: [], lastUpdatedAt: simulationNow() },
    salesSteps,
    appWizard: states.app_questionnaire === "done"
      ? home.appWizard ?? {
          progress: { done: 10, total: 10, percent: 100 },
          submitted: true,
          submittedAt: simulationNow()
        }
      : home.appWizard
  };
  const simulatedDelivery: DeliverablesResponse = {
    ...delivery,
    deliverables: simulatedDeliverables(delivery.deliverables, states),
    salespage: simulatedSalespageDelivery(delivery, states),
    salesSteps
  };
  return {
    home: simulatedHome,
    delivery: simulatedDelivery,
    resources: simulatedResources(states),
    milestones: simulatedMilestones(states)
  };
}

export function surfaceStateForStep(
  snapshot: ClientSimulationSnapshot,
  key: RailStepKey
): SectionState {
  if (key === "feedback" && snapshot.home.feedbackUnlocked === false) return "locked";
  if (key === "profile") {
    return snapshot.home.profileConfirmed === undefined
      ? snapshot.home.profile.status === "ready" ? "done" : "next"
      : snapshot.home.profileConfirmed ? "done" : "next";
  }
  if (key === "apple") {
    const app = snapshot.home.sections.find((section) => section.key === "app")?.state ?? "locked";
    if (!snapshot.home.client.includesApp) return "done";
    return snapshot.home.appleAccountConfirmed === undefined
      ? app === "done" ? "done" : app === "working" ? "working" : "next"
      : snapshot.home.appleAccountConfirmed ? "done" : "next";
  }
  const salesKey = key === "salespage_publish" ? "publish" : key === "app_release" ? "release" : key;
  if (["strategy", "salespage", "payments", "emails", "webinar", "stripe", "app_questionnaire", "publish", "handover", "release"].includes(salesKey)) {
    return snapshot.delivery.salesSteps?.find((step) => step.key === salesKey)?.state ?? "locked";
  }
  const sectionKey = railBackingSection(key, journeyAxesOf(snapshot.home.client));
  return snapshot.home.sections.find((section) => section.key === sectionKey)?.state ?? "locked";
}

function simulationStates(
  focus: RailStepKey,
  preserveFocusedQuestionnaireDone = false,
  axes: JourneyAxes = { includesApp: true, journeyMode: "full" }
): Record<RailStepKey, SectionState> {
  const states = Object.fromEntries(RAIL_STEP_KEYS.map((key) => [key, "locked"])) as Record<RailStepKey, SectionState>;
  const set = (state: SectionState, keys: RailStepKey[]) => {
    for (const key of keys) states[key] = state;
  };
  const finish = () => {
    if (preserveFocusedQuestionnaireDone && (focus === "q1" || focus === "q2")) {
      states[focus] = "done";
    }
    return states;
  };

  if (["onboarding_call", "profile", "apple"].includes(focus)) {
    set("next", ["onboarding_call", "profile", "apple"]);
    // 🔴 F152-W2: the SAME focus means two different pictures. In the full
    // journey the profile is step 2 of chapter 1 and the marketing video is
    // fourteen steps away, so `locked` is the truth. In the sales_only journey
    // the profile sits BEHIND that video, so a walkthrough that drew it locked
    // would show the client a step they have already watched as still ahead of
    // them, and their own rail would start on a padlock.
    if (axes.journeyMode === "sales_only") {
      states.marketing_call = "done";
      states.q2 = "next";
    }
    return finish();
  }

  set("done", ["onboarding_call", "profile"]);
  if (focus === "q1") {
    states.apple = "working";
    states.q1 = "next";
    return finish();
  }
  if (focus === "knowhow") {
    states.apple = "working";
    set("next", ["q1", "knowhow"]);
    return finish();
  }

  set("done", ["apple", "q1", "knowhow"]);
  if (focus === "training") {
    states.training = "working";
    return finish();
  }

  states.training = "done";
  if (["beta", "feedback", "marketing_call"].includes(focus)) {
    set("working", ["beta", "feedback"]);
    if (focus === "beta" || focus === "feedback") states.feedback = "locked";
    states.marketing_call = "next";
    return finish();
  }

  set("working", ["beta", "feedback"]);
  states.marketing_call = "done";
  if (focus === "q2") {
    states.q2 = "next";
    return finish();
  }

  states.q2 = "done";
  if (focus === "strategy") {
    states.strategy = "next";
    return finish();
  }

  states.strategy = "done";
  set("next", ["salespage", "emails", "webinar"]);
  states.stripe = "working";
  if (["salespage", "emails", "webinar", "stripe"].includes(focus)) return finish();

  states.salespage = "done";
  // F171: the payments step opens on the approved page; the client's turn
  // (give access) until the stage where the hidden stripe step closes too.
  states.payments = "next";
  if (focus === "payments") return finish();
  // F120: the step is done only once all three of its phases are, so the
  // simulated journey opens the (hidden) publication step in the same move -
  // that is what makes `simulatedSalespageDelivery` show integrated links and
  // an unlocked take-away beside a done sales page step.
  states.salespage_publish = "next";
  if (focus === "app_questionnaire") {
    states.app_questionnaire = "next";
    return finish();
  }

  states.stripe = "done";
  states.payments = "done";
  states.app_questionnaire = "done";
  set("next", ["salespage_publish", "handover"]);
  if (focus === "salespage_publish" || focus === "handover") return finish();

  set("done", ["emails", "webinar", "salespage_publish", "handover"]);
  states.app_release = "working";
  return finish();
}

function simulationPrimaryKey(
  states: Record<RailStepKey, SectionState>,
  focus: RailStepKey
): RailStepKey {
  if (states[focus] === "next") return focus;
  return RAIL_STEP_KEYS.find((key) => states[key] === "next") ?? focus;
}

function sharedState(left: SectionState, right: SectionState): SectionState {
  if (left === "done" && right === "done") return "done";
  if (left === "next" || right === "next") return "next";
  if (left === "working" || right === "working") return "working";
  return "locked";
}

function chapterState(states: SectionState[]): SectionState {
  if (states.every((state) => state === "done")) return "done";
  if (states.every((state) => state === "locked")) return "locked";
  return "working";
}

function appSectionState(states: Record<RailStepKey, SectionState>): SectionState {
  if (states.apple === "next") return "next";
  if (states.app_release === "done") return "done";
  if (states.apple === "done" || states.app_questionnaire !== "locked" || states.app_release !== "locked") return "working";
  return "locked";
}

// Mirrors the server's buildSalesSteps shape: since F82 the stripe and
// publish steps are not emitted (publication is phase 3 of the sales page
// step, the stripe step is hidden).
function salesStepsFromStates(
  states: Record<RailStepKey, SectionState>,
  journeyKeys: readonly RailStepKey[]
): SalesStepDto[] {
  // F171: the caller passes THIS client's journey (`railJourneyKeysFor`), so a
  // client with the payment setup keeps the payments step in the walkthrough.
  const journey = new Set(journeyKeys);
  // N15: the walkthrough builds its own salesSteps, so a mode it does not read
  // would show a sales_only client the Předání step the live payload never
  // carries. The rail's own filter is the judge, in the rail's vocabulary
  // (`app_release` is the sales list's `release`).
  return ([
    { key: "strategy", state: states.strategy, rail: "strategy" },
    { key: "salespage", state: states.salespage, rail: "salespage" },
    { key: "payments", state: states.payments, rail: "payments" },
    { key: "emails", state: states.emails, rail: "emails" },
    { key: "webinar", state: states.webinar, rail: "webinar" },
    { key: "app_questionnaire", state: states.app_questionnaire, rail: "app_questionnaire" },
    { key: "handover", state: states.handover, rail: "handover" },
    { key: "release", state: states.app_release, rail: "app_release" }
  ] as Array<SalesStepDto & { rail: RailStepKey }>)
    .filter((step) => journey.has(step.rail))
    .map(({ key, state }) => ({ key, state }));
}

function simulatedDeliverables(
  current: DeliverableDto[],
  states: Record<RailStepKey, SectionState>
): DeliverableDto[] {
  const byKind = new Map(current.map((item) => [item.kind, item]));
  const ensure = (kind: DeliverableDto["kind"], state: SectionState) => {
    if (state === "locked" || state === "working") return;
    const existing = byKind.get(kind);
    if (existing) {
      const done = state === "done";
      byKind.set(kind, {
        ...existing,
        status: done ? "approved" : "review",
        published: true,
        publishedAt: existing.publishedAt ?? simulationNow(),
        approvedBy: done ? (existing.approvedBy ?? "simulation") : null,
        approvedAt: done ? (existing.approvedAt ?? simulationNow()) : null,
        updatedAt: simulationNow()
      });
      return;
    }
    byKind.set(kind, {
      id: `simulation-${kind}`,
      kind,
      version: 1,
      title: `simulation-${kind}`,
      url: `https://launchpad.local/${kind}`,
      status: state === "done" ? "approved" : "review",
      published: true,
      publishedAt: simulationNow(),
      approvedBy: state === "done" ? "simulation" : null,
      approvedAt: state === "done" ? simulationNow() : null,
      createdAt: simulationNow(),
      updatedAt: simulationNow(),
      contentMd: null
    });
  };
  ensure("plan", states.strategy);
  ensure("salespage", states.salespage);
  ensure("emails", states.emails);
  ensure("webinar", states.webinar);
  return [...byKind.values()];
}

function simulatedSalespageDelivery(
  delivery: DeliverablesResponse,
  states: Record<RailStepKey, SectionState>
): DeliverablesResponse["salespage"] {
  // F82: an unlocked publication phase means the order links are already
  // worked into the page, so the walkthrough shows the block integrated.
  const integrated = states.salespage_publish === "next" || states.salespage_publish === "done";
  const orderLinks: OrderLinksDto | null = states.salespage === "done"
    ? {
        pageApproved: true,
        custom: false,
        tariffs: [
          { id: "rocni", name: "Roční tarif", url: integrated ? "https://buy.stripe.com/rocni-tarif" : null },
          { id: "mesicni", name: "Měsíční tarif", url: integrated ? "https://buy.stripe.com/mesicni-tarif" : null }
        ],
        confirmed: integrated,
        integration: null,
        integratedAt: integrated ? simulationNow() : null,
        legacyStripeUnlocked: false,
        // F99: the simulated page states its own tariffs, exactly like a real
        // approved one, so the walkthrough never shows the manual mode.
        source: "page",
        manualAvailable: false
      }
    : null;
  if (states.salespage_publish === "done") {
    return {
      locked: false,
      orderLinks,
      netlify: {
        siteUrl: "https://launchpad.local/prodej",
        dnsTarget: "launchpad-local.netlify.app",
        publishedVersion: 1,
        publishedAt: simulationNow(),
        tokenHint: "test"
      }
    };
  }
  if (states.salespage_publish === "next") return { locked: false, orderLinks, netlify: null };
  return delivery.salespage ?? { locked: true, orderLinks, netlify: null };
}

function simulatedResources(states: Record<RailStepKey, SectionState>): ResourceDto[] {
  const resources: ResourceDto[] = [];
  const add = (key: ResourceDto["key"], value: Record<string, unknown>) => {
    resources.push({ key, value, updatedAt: simulationNow() });
  };
  if (states.profile === "done" || states.apple === "done") {
    add("step_confirmations", {
      ...(states.profile === "done" ? { profile: { confirmed: true, confirmed_at: simulationNow() } } : {}),
      ...(states.apple === "done" ? { apple: { confirmed: true, confirmed_at: simulationNow() } } : {})
    });
  }
  if (states.knowhow === "next" || states.knowhow === "done") {
    add("knowhow_google_account", {
      email: "test@buddypro.cz",
      requested_at: simulationNow()
    });
    add("drive_folder", {
      url: "https://drive.google.com/drive/folders/walkthrough",
      label: "Složka s know-how"
    });
    add("url_doc", {
      url: "https://docs.google.com/document/d/walkthrough/edit",
      label: "Odkazy na videa a podcasty"
    });
  }
  if (states.training !== "locked") {
    add("trial_link", {
      url: "https://t.me/buddypro_test_bot",
      invite_url: "https://t.me/buddypro_test_bot?start=tester",
      invite_limit: 10,
      bot_username: "buddypro_test_bot"
    });
  }
  if (states.marketing_call === "done") {
    add("handover_telegram", {
      username: "klient",
      ...(states.handover !== "locked" ? {} : { telegram_id: "123456789" }),
      started: true
    });
    add("bot_transfer_state", {
      state: "transferred",
      next_actor: "none",
      transferred_at: simulationNow()
    });
  }
  if (states.stripe !== "locked") {
    add("stripe_setup", states.stripe === "done"
      ? { verified_receiving: true, client_invited_team: true }
      : { status: "preparing" });
  }
  if (states.apple !== "next" || states.app_questionnaire !== "locked" || states.app_release !== "locked") {
    add("app_state", {
      apple_account: {
        two_factor_enabled: true,
        account_type: "individual",
        duns_required: false,
        duns_confirmed: false,
        fee_confirmed: true,
        registration_status: states.apple === "done" ? "active" : "submitted"
      },
      build: states.app_release === "working" ? "approved" : "not_started",
      app_store_review: states.app_release === "working" ? "submitted" : "not_started",
      google_play_review: states.app_release === "working" ? "submitted" : "not_started",
      store_urls: {}
    });
  }
  return resources;
}

function simulatedMilestones(states: Record<RailStepKey, SectionState>): MilestoneDto[] {
  const milestones: MilestoneDto[] = [];
  const add = (key: MilestoneDto["key"], evidence: Record<string, unknown>) => {
    milestones.push({ key, reachedAt: simulationNow(), setBy: "simulation", evidence });
  };
  if (states.marketing_call === "done") add("bot_transferred", { ownership_confirmed: true });
  if (states.stripe === "done") add("stripe_receiving", { verified: true });
  if (states.salespage_publish === "next" || states.salespage_publish === "done") {
    add("order_links_integrated", { jobId: "simulation" });
  }
  if (states.salespage_publish === "done") add("assets_handed", { published: true });
  if (states.app_release === "done") add("app_live", { published: true });
  return milestones;
}

function simulationNow(): string {
  return "2026-07-28T10:00:00.000Z";
}
